import { useState } from "react";
import { toast } from "sonner";
import { Leaf, Lightbulb, Minus, Plus, RotateCcw, Sparkles, Wand2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errMsg } from "@/lib/api";
import { lineText, scaleIngredient, unitLabel } from "@/lib/recipes/scale";
import { Locked } from "@/components/recipes/NutritionPanel";

export const Scaler = ({ base, value, onChange }) => {
  const set = (n) => onChange(Math.min(10000, Math.max(1, n || 1)));
  return (
    <div className="flex flex-wrap items-center gap-2" data-testid="recipe-scaler">
      <span className="lf-label">Servings</span>
      <div className="flex items-center rounded-full border border-[#2D3442]">
        <button onClick={() => set(value - 1)} className="rounded-l-full p-1.5 text-[#D6CCBC] hover:bg-[#1A202C]" aria-label="Fewer servings" data-testid="recipe-servings-minus"><Minus className="h-3.5 w-3.5" /></button>
        <input type="number" min={1} value={value} onChange={(e) => set(parseInt(e.target.value, 10))} className="w-14 bg-transparent text-center text-sm tabular-nums text-[#F3EBDD] outline-none" data-testid="recipe-servings-target" />
        <button onClick={() => set(value + 1)} className="rounded-r-full p-1.5 text-[#D6CCBC] hover:bg-[#1A202C]" aria-label="More servings" data-testid="recipe-servings-plus"><Plus className="h-3.5 w-3.5" /></button>
      </div>
      {[2, 3, 5, 10].map((x) => <button key={x} onClick={() => set(base * x)} className="rounded-full px-2 py-0.5 text-xs text-[#A8A090] hover:bg-[#1A202C] hover:text-[#F3EBDD]" data-testid={`recipe-scale-x${x}`}>×{x}</button>)}
      {value !== base && <button onClick={() => set(base)} className="inline-flex items-center gap-1 text-xs text-[#F59E0B] hover:underline" data-testid="recipe-scale-reset"><RotateCcw className="h-3 w-3" />Original ({base})</button>}
    </div>
  );
};

export const IngredientList = ({ recipe, factor, adjusted, subs }) => (
  <>
  <ul className="space-y-2" data-testid="recipe-ingredients">
    {(recipe.ingredients || []).map((ing, i) => {
      const s = scaleIngredient(ing, factor);
      const adj = adjusted?.[ing.id];
      const sub = subs?.[ing.id];
      return (
        <li key={ing.id} className="flex gap-3 text-[15px] leading-snug" data-testid={`recipe-ing-${i}`}>
          <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#D97706]" />
          <span className="min-w-0">
            <span className={s.changed ? "text-[#FBBF24]" : "text-[#F3EBDD]"} data-testid={`recipe-ing-amount-${i}`}>{[s.qty, s.changed ? unitLabel(s.unit, s.qty) : s.unit].filter(Boolean).join(" ")}</span>
            {" "}<span className="text-[#E8DFD1]">{ing.name}</span>{ing.note && <span className="text-[#A8A090]">, {ing.note}</span>}
            {ing.fictional && <span className="ml-1.5 inline-flex items-center gap-0.5 rounded bg-[#C084FC]/15 px-1.5 text-[10px] uppercase tracking-wider text-[#D8B4FE]" title={ing.fiction_desc}><Wand2 className="h-2.5 w-2.5" />made-up</span>}
            {adj && <span className="block text-xs text-[#A8A090]" title={adj.why} data-testid={`recipe-ing-adjusted-${i}`}>Kitchen-friendly: <span className="text-[#F3EBDD]">{adj.display}</span></span>}
            {sub && <span className="block text-xs text-[#86EFAC]" data-testid={`recipe-ing-sub-${i}`}>Real-life swap: {sub.amount} {sub.substitute}{sub.alt ? ` (or ${sub.alt})` : ""}<span className="text-[#7A7468]"> · {sub.why}</span></span>}
          </span>
        </li>
      );
    })}
  </ul>
  {subs && factor !== 1 && <p className="text-[11px] text-[#7A7468]" data-testid="recipe-subs-basis">Real-life swap amounts are for the original {recipe.servings} servings.</p>}
  </>
);

export const ScaleTips = ({ recipe, servings, paid, tips, setTips }) => {
  const [busy, setBusy] = useState(false);
  const factor = servings / recipe.servings;
  const run = async () => {
    setBusy(true);
    const scaled = recipe.ingredients.map((i) => { const s = scaleIngredient(i, factor); return { id: i.id, text: lineText(s.qty, s.unit, i) }; });
    try { setTips((await api.post(`/recipes/${recipe.id}/scale-tips`, { servings, scaled })).data); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  if (servings === recipe.servings || !recipe.ingredients?.length) return null;
  if (!paid) return <Locked what="AI scaling tips" testid="scale-tips-locked" />;
  if (!tips || tips.servings !== servings) return <Button size="sm" variant="outline" onClick={run} disabled={busy} data-testid="scale-tips-button"><Lightbulb className="mr-1 h-3.5 w-3.5" />{busy ? "Thinking…" : `Cooking tips for ${servings} servings`}</Button>;
  return (
    <div className="space-y-2 rounded-md border border-[#D97706]/30 bg-[#D97706]/5 p-4 animate-in fade-in duration-300" data-testid="scale-tips">
      <div className="flex items-center gap-1.5 text-sm text-[#FBBF24]"><Lightbulb className="h-4 w-4" />Tips for {servings} servings</div>
      <ul className="list-disc space-y-1 pl-5 text-sm text-[#D6CCBC]">{tips.tips.map((t, i) => <li key={i} data-testid={`scale-tip-${i}`}>{t}</li>)}</ul>
      {!tips.tips.length && <p className="text-sm text-[#A8A090]">This recipe should scale evenly.</p>}
    </div>
  );
};

export const Substitutes = ({ recipe, paid, subs, setSubs }) => {
  const [busy, setBusy] = useState(false);
  if (!recipe.ingredients?.some((i) => i.fictional)) return null;
  const run = async () => {
    setBusy(true);
    try { const { data } = await api.post(`/recipes/${recipe.id}/substitutes`); setSubs(Object.fromEntries(data.subs.map((s) => [s.id, s]))); toast.success("Real-life swaps added to the ingredient list"); } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };
  return (
    <div className="space-y-2" data-testid="substitutes">
      {!paid ? <Locked what="Real-life substitutes" testid="subs-locked" /> : (
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" variant="outline" onClick={run} disabled={busy} data-testid="subs-button"><Leaf className="mr-1 h-3.5 w-3.5" />{busy ? "Finding swaps…" : subs ? "Find swaps again" : "Cook it in real life"}</Button>
          <span className="text-xs text-[#7A7468]">Suggests store-bought stand-ins for made-up ingredients.</span>
        </div>
      )}
    </div>
  );
};

export const NoAiNote = () => <p className="flex items-center gap-1 text-[11px] text-[#7A7468]"><Sparkles className="h-3 w-3" />LoreRight's AI never writes recipes; it only works with yours.</p>;
